import React, { useState, useRef } from "react";
import AsyncSelect from "react-select/async";
import useErrorHandler from "@beeldit/core/errors/useErrorHandler";
import index from "@beeldit/core/services";
import { Pet } from "../models/Pet";

interface Option {
  label: string;
  value: number | string;
}

function CustomAsyncSelectWidget(props: any) {
  const { id, value, required, disabled, readonly, onChange, formContext } = props;
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [inputValue, setInputValue] = useState(""); // Estado para manejar la entrada del usuario
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const errorHandler = useErrorHandler();

  const { tableFilters } = formContext;

  // Cargar opciones con debounce
  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return new Promise((resolve) => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

      debounceTimeout.current = setTimeout(async () => {
        if (!inputValue.trim()) {
          resolve([]); // No buscar si el input está vacío
          return;
        }

        try {
          let queryParams = "";
          if (tableFilters && tableFilters.checked_out_from && tableFilters.checked_out_to) {
            // Si hay filtros de fecha, los añadimos a la búsqueda
            queryParams = `&checked_out_from=${tableFilters.checked_out_from}&checked_out_to=${tableFilters.checked_out_to}`;
          }
          // lo mismo pero con checked_id
          if (tableFilters && tableFilters.checked_in_from && tableFilters.checked_in_to) {
            queryParams = `&checked_in_from=${tableFilters.checked_in_from}&checked_in_to=${tableFilters.checked_in_to}`;
          }
          
          const response = await index("pets", `search=${inputValue}${queryParams}`);
          const options = response.data.elements.map((pet: Pet) => ({
            label: `${pet.name} (${pet.breed})${pet.microchip ? ` - ${pet.microchip}` : ""}`,
            value: pet.id,
          }));

          resolve(options);
        } catch (error) {
          errorHandler(error, null);
          resolve([]); // Evitamos que la promesa quede colgada
        }
      }, 800); // Espera 800ms antes de ejecutar la búsqueda
    });
  };

  // Manejo del cambio de selección
  const handleOnChange = (newValue: Option | null) => {
    setSelectedOption(newValue);
    onChange(newValue?.value ?? null);
  };

  // Captura de entrada en el input
  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
  };

  // Mensaje personalizado cuando no hay opciones
  const noOptionsMessage = () => {
    return inputValue.trim()
      ? "No se encontraron mascotas"
      : "Escriba el nombre o microchip de la mascota";
  };

  // Estilos del selector
  const selectStyles = {
    control: (provided: any) => ({
      ...provided,
      minWidth: "400px",
      borderColor: "#E7F5F2",
      backgroundColor: "#E7F5F2",
      borderRadius: "0.25rem",
    }),
  };

  return (
    <AsyncSelect
      id={id}
      value={selectedOption}
      loadOptions={loadOptions} // Ahora usa debounce correctamente
      onChange={handleOnChange}
      onInputChange={handleInputChange} // Capturamos el input del usuario
      styles={selectStyles}
      placeholder="Escribe para buscar mascotas..."
      isClearable={true}
      cacheOptions
      isDisabled={disabled || readonly}
      noOptionsMessage={noOptionsMessage} // Mensaje personalizado cuando no hay opciones
    />
  );
}

// Componente final que usa el selector asíncrono
function PetSelector(props: any) {
  const { id, value, required, disabled, readonly, onChange, formContext } = props;
  return (
    <CustomAsyncSelectWidget
      id={id}
      value={value}
      required={required}
      disabled={disabled}
      readonly={readonly}
      onChange={onChange}
      formContext={formContext}
    />
  );
}

export default PetSelector;
